import React from 'react';

import {
  faCalendar,
  faChartBar,
  faChevronDown,
  faClipboardList,
  faGear,
  faPlug,
  faRightFromBracket,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Group, Text, Button, Container, Burger, Drawer, Stack, Box, Menu, Avatar, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Link } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';

interface HeaderProps {
  logoText?: string;
  menuItems?: Array<{
    label: string;
    onClick?: () => void;
    href?: string;
  }>;
}

export const Header: React.FC<HeaderProps> = ({
  logoText = 'Phono',
  menuItems = [
    { label: 'Home', href: '/' },
    { label: 'Clients', href: '/clients' },
    { label: 'Callers', href: '/callers' },
    { label: 'Care Managers', href: '/care_managers' }
  ]
}) => {
  const { logout, user, account } = useAuth();
  const [opened, { toggle, close }] = useDisclosure(false);
  
  // Helper function to get user display name
  const getUserDisplayName = () => {
    if (!user) return 'User';
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user.firstName) return user.firstName;
    if (user.lastName) return user.lastName;
    return user.email.split('@')[0]; // Fallback to email username
  };
  
  const getUserInitials = () => {
    if (!user) return 'U';
    if (user.firstName && user.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
    }
    if (user.firstName) return user.firstName.charAt(0).toUpperCase();
    if (user.lastName) return user.lastName.charAt(0).toUpperCase();
    return user.email.charAt(0).toUpperCase();
  };

  return (
    <>
      <Container size="xl" style={{ height: '100%' }}>
        <Group justify="space-between" style={{ height: '100%' }}>
          {/* Logo Section */}
          <Group>
            <Text
              component={Link}
              fw={700}
              size="xl"
              style={{
                background: 'linear-gradient(45deg, #228be6, #40c057)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textDecoration: 'none',
                cursor: 'pointer'
              }}
              to="/"
            >
              {logoText} {account && `for ${account.name}`}
            </Text>
          </Group>

          {/* Desktop Menu Items */}
          <Group gap="sm" visibleFrom="sm">
            {menuItems.map((item) => (
              item.href ? (
                <Button
                  key={`menu-item-${item.label}`}
                  color="gray"
                  component={Link}
                  size="sm"
                  style={{
                    textDecoration: 'none',
                    color: 'inherit'
                  }}
                  to={item.href}
                  variant="subtle"
                >
                  {item.label}
                </Button>
              ) : (
                <Button
                  key={`menu-item-${item.label}`}
                  color="gray"
                  size="sm"
                  style={{
                    textDecoration: 'none',
                    color: 'inherit'
                  }}
                  variant="subtle"
                  onClick={item.onClick}
                >
                  {item.label}
                </Button>
              )
            ))}
            
            {/* User Menu */}
            <Menu shadow="md" width={200}>
              <Menu.Target>
                <UnstyledButton
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--mantine-color-gray-1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <Avatar color="blue" size="sm">
                    {getUserInitials()}
                  </Avatar>
                  <Text fw={500} size="sm">
                    {getUserDisplayName()}
                  </Text>
                  <FontAwesomeIcon icon={faChevronDown} size="sm" />
                </UnstyledButton>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Item
                  component={Link}
                  leftSection={<FontAwesomeIcon icon={faGear} size="sm" />}
                  to="/settings"
                >
                  User Settings
                </Menu.Item>
                {user?.isAdmin && (
                  <Menu.Item
                    component={Link}
                    leftSection={<FontAwesomeIcon icon={faUsers} size="sm" />}
                    to="/account/manage"
                  >
                    Manage Account
                  </Menu.Item>
                )}
                {user?.isAdmin && (
                  <Menu.Item
                    component={Link}
                    leftSection={<FontAwesomeIcon icon={faPlug} size="sm" />}
                    to="/account/integrations"
                  >
                    Manage Integrations
                  </Menu.Item>
                )}
                <Menu.Item
                  component={Link}
                  leftSection={<FontAwesomeIcon icon={faCalendar} size="sm" />}
                  to="/visits"
                >
                  View Visits
                </Menu.Item>
                {user?.isAdmin && (
                  <Menu.Item
                    component={Link}
                    leftSection={<FontAwesomeIcon icon={faChartBar} size="sm" />}
                    to="/encounter/report"
                  >
                    Encounter Report
                  </Menu.Item>
                )}
                <Menu.Item
                  component={Link}
                  leftSection={<FontAwesomeIcon icon={faClipboardList} size="sm" />}
                  to="/surveys"
                >
                  Manage Surveys
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  color="red"
                  leftSection={<FontAwesomeIcon icon={faRightFromBracket} size="sm" />}
                  onClick={() => void logout()}
                >
                  Logout
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>

          {/* Mobile Hamburger Menu */}
          <Burger
            hiddenFrom="sm"
            opened={opened}
            size="sm"
            onClick={toggle}
          />
        </Group>
      </Container>

      {/* Mobile Drawer Menu */}
      <Drawer
        hiddenFrom="sm"
        opened={opened}
        position="right"
        size="xs"
        title="Menu"
        onClose={close}
      >
        <Stack gap="md">
          {/* User Info */}
          <Box p="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
            <Group>
              <Avatar color="blue" size="md">
                {getUserInitials()}
              </Avatar>
              <div>
                <Text fw={500}>{getUserDisplayName()}</Text>
                <Text c="dimmed" size="sm">{user?.email || ''}</Text>
              </div>
            </Group>
          </Box>

          {/* Menu Items */}
          {menuItems.map((item) => (
            item.href ? (
              <Button
                key={`mobile-menu-item-${item.label}`}
                fullWidth
                color="gray"
                component={Link}
                style={{
                  textDecoration: 'none',
                  color: 'inherit'
                }}
                to={item.href}
                variant="subtle"
                onClick={close}
              >
                {item.label}
              </Button>
            ) : (
              <Button
                key={`mobile-menu-item-${item.label}`}
                fullWidth
                color="gray"
                style={{
                  textDecoration: 'none',
                  color: 'inherit'
                }}
                variant="subtle"
                onClick={() => {
                  item.onClick?.();
                  close();
                }}
              >
                {item.label}
              </Button>
            )
          ))}
          
          {/* Settings */}
          <Button
            fullWidth
            color="gray"
            component={Link}
            style={{
              textDecoration: 'none',
              color: 'inherit'
            }}
            to="/settings"
            variant="subtle"
            onClick={close}
          >
            User Settings
          </Button>
          
          {/* Manage Account - Admin Only */}
          {user?.isAdmin && (
            <Button
              fullWidth
              color="gray"
              component={Link}
              style={{
                textDecoration: 'none',
                color: 'inherit'
              }}
              to="/account/manage"
              variant="subtle"
              onClick={close}
            >
              Manage Account
            </Button>
          )}
          
          {/* Manage Integrations - Admin Only */}
          {user?.isAdmin && (
            <Button
              fullWidth
              color="gray"
              component={Link}
              style={{
                textDecoration: 'none',
                color: 'inherit'
              }}
              to="/account/integrations"
              variant="subtle"
              onClick={close}
            >
              Manage Integrations
            </Button>
          )}
          
          {/* View Visits */}
          <Button
            fullWidth
            color="gray"
            component={Link}
            style={{
              textDecoration: 'none',
              color: 'inherit'
            }}
            to="/visits"
            variant="subtle"
            onClick={close}
          >
            View Visits
          </Button>

          {/* Encounter Report - Admin Only */}
          {user?.isAdmin && (
            <Button
              fullWidth
              color="gray"
              component={Link}
              style={{
                textDecoration: 'none',
                color: 'inherit'
              }}
              to="/encounter/report"
              variant="subtle"
              onClick={close}
            >
              Encounter Report
            </Button>
          )}

          {/* Manage Survey */}
          <Button
            fullWidth
            color="gray"
            component={Link}
            style={{
              textDecoration: 'none',
              color: 'inherit'
            }}
            to="/surveys"
            variant="subtle"
            onClick={close}
          >
            Manage Surveys
          </Button>
          
          {/* Logout */}
          <Box mt="md">
            <Button
              fullWidth
              color="red"
              variant="subtle"
              onClick={() => {
                void logout();
                close();
              }}
            >
              Logout
            </Button>
          </Box>
        </Stack>
      </Drawer>
    </>
  );
};

export default Header;
